import React, { useState, useRef } from 'react'
import t from 'prop-types'
import { graphql, useStaticQuery } from 'gatsby'
import axios from 'axios'
import InputMask from 'react-input-mask'
import FormData from 'form-data'
import { useForm } from 'react-hook-form'
import {
  useGoogleReCaptcha,
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from 'react-google-recaptcha-v3'
import Button from '../Links/ButtonPrimary'
import { styled } from 'linaria/react'
import Form from '../Form/Form'

const SubmitSpinner = styled.svg`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const scrollToRef = ref => {
  window.scrollTo({
    left: 0,
    top: ref.current.offsetTop,
    behavior: 'smooth',
  })
}

const ContactForm = ({ slug }) => {
  const data = useStaticQuery(graphql`
    {
      page: wpPage(slug: { eq: "contact" }) {
        id
        acfForm {
          footer
          formId
          formIntro
          header
        }
      }
    }
  `)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [message, setMessage] = useState('')

  const messageRef = useRef(null)
  const executeScroll = () => scrollToRef(messageRef)

  const { register, handleSubmit, errors } = useForm({
    mode: 'onBlur',
  })

  const { executeRecaptcha } = useGoogleReCaptcha()
  const [token, setToken] = useState('')

  const onSubmit = async formData => {
    setIsSubmitting(true)

    if (!token) {
      const result = await executeRecaptcha()
      setToken(result)

      if (!result) {
        return
      }
    }

    let formFields = new FormData()
    for (const field in formData) {
      formFields.append(field, formData[field])
    }
    formFields.append('_wpcf7_recaptcha_response', token)
    formFields.append('slug', slug)

    const requestOptions = {
      method: 'post',
      url: `${process.env.GATSBY_WORDPRESS_URL}/wp-json/contact-form-7/v1/contact-forms/${data?.page?.acfForm?.formId}/feedback`,
      headers: { 'Content-Type': 'application/json' },
      data: formFields,
    }

    axios(requestOptions)
      .then(function(response) {
        setSubmitted(true)
        setMessage(response.data.message)
      })
      .catch(function(error) {
        console.log(error)
        setSubmitted(false)
      })
      .then(function() {
        setIsSubmitting(false)
        executeScroll()
      })
  }

  const onError = (errors, e) => {
    console.log(errors, e)
    if (errors.firstName) {
      errors.firstName.message = 'Required'
      errors.firstName.css = 'bg-red-100'
    }
    if (errors.lastName) {
      errors.lastName.message = 'Required'
      errors.lastName.css = 'bg-red-100'
    }
    if (errors.email) {
      errors.email.message = 'Required'
      errors.email.css = 'bg-red-100'
    }
    if (errors.email) {
      errors.email.message = 'Must be a valid email address'
      errors.email.css = 'bg-red-100'
    }
    if (errors.budget) {
      errors.budget.message = 'Required'
      errors.budget.css = 'bg-red-100'
    }
    if (errors.moreInfo) {
      errors.moreInfo.message = 'Please let us know what you are thinking'
      errors.moreInfo.css = 'bg-red-100'
    }
  }

  return (
    <div className="max-w-lg mx-auto lg:mx-0">
      <div className="form md:min-h-sm flex flex-col">
        <div
          id="contact-form-success"
          className={'message' + submitted ? 'visible' : 'invisible'}
          ref={messageRef}
        >
          {message}
        </div>
        <Form
          onSubmit={handleSubmit(async data => onSubmit(data), onError)}
          method="post"
          className="pt-12"
          style={{
            overflow: submitted ? 'hidden' : 'visible',
            height: 'auto',
            maxHeight: submitted ? '0' : '1000px',
            transition: 'max-height 0.5s ease-out',
          }}
        >
          <div className="block pb-4 mb-2">
            <label
              className="block text-gray-600 text-base pb-1"
              htmlFor="firstName"
            >
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              placeholder=""
              className={`form-input ${errors?.firstName?.css}`}
              defaultValue=""
              ref={register({ required: true })}
            />
          </div>

          <div className="block pb-4 mb-2">
            <label
              className="block text-gray-600 text-base pb-1"
              htmlFor="lastName"
            >
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              placeholder=""
              className={`form-input ${errors?.lastName?.css}`}
              defaultValue=""
              ref={register({ required: true })}
            />
          </div>
          <div className="block pb-4 mb-2">
            <label
              className="block text-gray-600 text-base pb-1"
              htmlFor="phone"
            >
              Phone (optional)
            </label>
            <InputMask
              mask="(999) 999-9999"
              maskChar=" "
              type="tel"
              autoComplete="tel"
              id="phone"
              name="phone"
              placeholder=""
              className="form-input"
              defaultValue=""
              inputRef={register}
            />
          </div>
          <div className="block pb-4 mb-2">
            <label
              className="block text-gray-600 text-base pb-1"
              htmlFor="email"
            >
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              autoCapitalize="none"
              placeholder=""
              className={`form-input ${errors?.email?.css}`}
              defaultValue=""
              ref={register({ required: true })}
            ></input>
          </div>

          <div className="block pb-4 mb-2">
            <label
              className="block text-gray-600 text-base pb-1"
              htmlFor="budget"
            >
              Budget Range
            </label>
            <div className="inline-block relative w-full">
              <select
                name="budget"
                placeholder="Select"
                className={`form-input ${errors?.budget?.css}`}
                defaultValue=""
                ref={register({ required: true })}
                required
                readOnly
              >
                <option value="" disabled hidden>
                  Select
                </option>
                <option value="20k">$20k-$50k</option>
                <option value="51k">$51k - $75k</option>
                <option value="76k">$76 - $100k</option>
                <option value="100k+">$100k+</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="block pb-4 mb-2">
            <label
              className="block text-gray-600 text-base pb-1"
              htmlFor="moreInfo"
            >
              Tell us more about your project
            </label>
            <textarea
              name="moreInfo"
              rows="4"
              placeholder=""
              className={`form-input resize-none ${errors?.moreInfo?.css}`}
              defaultValue=""
              ref={register({ required: true })}
            ></textarea>
          </div>
          <div className="flex flex-row-reverse">
            <Button
              style={{
                alignSelf: 'end',
                width: isSubmitting ? '50%' : '40%',
                transition: 'width 0.5s ease',
              }}
            >
              {isSubmitting && (
                <SubmitSpinner
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </SubmitSpinner>
              )}
              Send
            </Button>
          </div>

          <div className="text-gray-500 text-xs pt-8 pb-12">
            <div
              dangerouslySetInnerHTML={{
                __html: data?.page?.acfForm?.footer,
              }}
            />
          </div>
          {'' === token && (
            <GoogleReCaptcha
              onVerify={token => {
                setToken(token)
              }}
            />
          )}
        </Form>
      </div>
      <div className="text-gray-600 py-8 text-lg">
        <div
          dangerouslySetInnerHTML={{
            __html: data?.page?.acfForm?.formIntro,
          }}
        />
      </div>
    </div>
  )
}

ContactForm.propTypes = {
  slug: t.string,
}

ContactForm.defaultProps = {
  slug: '',
}

export default ContactForm
